import { useState, type ChangeEvent } from 'react';
import {
  Alert, Box, Chip, Divider, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { PasSekcji } from '@/components/PasSekcji';
import { SlupkiRankingowe } from '@/components/wykresy/Wykresy';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { formatCount } from '@/lib/format';
import { useAuth } from '@/lib/auth/AuthContext';
import { StanOdswiezania } from '@/components/StanOdswiezania';
import { useDzisiejszeZamowienia } from '@/lib/useAnalytics';
import type { DzisWyjazd } from '@/lib/api/analytics';
import { chwilaWydruku, dataPl, dokumentWydruku, drukujDokument } from '@/lib/druk/wydrukPosilkow';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * DZISIEJSZE ZAMÓWIENIA — ekran, przy którym stoi się w kuchni.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Restauracja otwierając ten ekran ma NATYCHMIAST wiedzieć:
 *   ile zamówień · ile posiłków ugotować · do ilu szkół jedzie · ile do której ·
 *   czego ile przygotować · które danie do której szkoły.
 *
 * Żadnej z tych odpowiedzi nie wolno chować pod najazdem ani za kliknięciem — ekran bywa
 * używany na tablecie, w hałasie, przy pracy obiema rękami.
 *
 * ┌─ BEZ WYKRESÓW KOŁOWYCH ─────────────────────────────────────────────────────────────────────┐
 * │ Stary panel używa tu pierścieni (`doughnutChartType = 'doughnut'`). Właściciel zdecydował    │
 * │ o zamianie na słupki poziome. Powód jest operacyjny, nie estetyczny: z pierścienia nie da   │
 * │ się odczytać LICZBY SZTUK, a to jedyna rzecz, po którą kucharz tu sięga.                     │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * WYKRES NIGDY NIE ZASTĘPUJE LISTY. Pod każdym rankingiem stoją pełne liczby — słupek pokazuje
 * proporcję, tabela mówi, ile dokładnie.
 */

const DNI_SKROT = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];

function dzisiaj(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Godzina graniczna do podpisu, gdy serwer nie przysłał jeszcze pola `wyjazdy`. */
const GRANICA_DOMYSLNA = '10:00';

/**
 * Jeden z czterech wykresów podziału na wyjazdy.
 *
 * Wszystkie cztery są w KOLORZE MARKI. Rozróżnia je pozycja w siatce i podpis, nie barwa —
 * gdyby kolumny miały różne kolory, sugerowałyby, że mierzą różne wielkości, a obie mierzą
 * sztuki. `wykres-kuchnia` podnosi rozmiar etykiet: ten ekran czyta się z odległości ramienia.
 */
function WykresWyjazdu({ tytul, podtytul, pozycje, pusty, onDrukuj }: {
  tytul: string;
  podtytul: string;
  pozycje: { nazwa: string; wartosc: number; klucz?: string }[];
  pusty: string;
  /** Druk listy tej ramki (OWNER 19.10) — tylko ramki posiłków. */
  onDrukuj?: () => void;
}) {
  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ px: 1 }}>{tytul}</Typography>
        {onDrukuj ? (
          <Tooltip title={`Drukuj: ${tytul}`}>
            <IconButton aria-label={`Drukuj: ${tytul}`} onClick={onDrukuj} size="small"
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Typography variant="caption" sx={{ px: 1, color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {podtytul}
      </Typography>
      <Box className="wykres-kuchnia">
        <SlupkiRankingowe
          pozycje={pozycje}
          format={(v) => `${formatCount(v)} szt.`}
          kolor="var(--seria-1)"
          maks={12}
          komunikatPusty={pusty}
        />
      </Box>
    </Box>
  );
}

/** Podpis wyjazdu: ile szkół i ile posiłków — liczby, nie przymiotniki. */
function opisWyjazdu(w: DzisWyjazd | undefined, regula: string): string {
  const p = w?.podsumowanie;
  if (!p || p.pozycji === 0) return `${regula} — brak zamówień`;
  return `${regula} · ${formatCount(p.szkol)} ${p.szkol === 1 ? 'szkoła' : 'szkół'}`
    + ` · ${formatCount(p.posilkow)} szt.`;
}

function Kpi({ etykieta, wartosc, opis }: { etykieta: string; wartosc: number; opis?: string }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 140 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
      {/* Liczba ma być czytelna z odległości ramienia — to jest ekran roboczy, nie raport. */}
      <Typography variant="h4" sx={{ lineHeight: 1.1 }}>
        <AnimatedNumber value={wartosc} format={formatCount} />
      </Typography>
      {opis && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{opis}</Typography>}
    </Stack>
  );
}

/**
 * Druk listy posiłków jednego wyjazdu (OWNER 19.10) — MIGAWKA chwili kliknięcia: te same pozycje i ta sama kolejność
 * co ramka. Nagłówek sekcji jak w starym panelu: „Posiłki — zamówienia do/po 10:00”.
 */
function drukujPosilki(sekcja: string, pozycje: { nazwa: string; wartosc: number }[], restauracja: string, dzien: string) {
  drukujDokument(dokumentWydruku({
    restauracja, data: dataPl(dzien), wydrukowano: chwilaWydruku(), sekcja,
    pozycje: pozycje.map((x) => ({ nazwa: x.nazwa, ilosc: x.wartosc })),
  }));
}

export function DzisiejszeZamowieniaPage() {
  const auth = useAuth();
  const [dzien, setDzien] = useState(dzisiaj());

  /*
   * Konto restauracji ma własny zakres i niczego nie wskazuje. Właściciel MUSI wskazać, którą
   * restaurację ogląda — serwer i tak sprawdza to wskazanie względem tożsamości, więc pole
   * poniżej jest wygodą, a nie uprawnieniem.
   */
  // `admin.users.read` ma wyłącznie rola administratora systemu głównego — czyli właściciel.
  // Pytamy o UPRAWNIENIE, a nie o nazwę roli: nazwy ról bywają przemapowywane, uprawnienia nie.
  const czyWlasciciel = auth.has('admin.users.read');
  const [restauracja, setRestauracja] = useState<string>('');

  const wskazana = restauracja.trim() === '' ? undefined : Number(restauracja);
  const zapytanie = useDzisiejszeZamowienia(wskazana, dzien);
  const { data, isLoading, error } = zapytanie;

  const p = data?.podsumowanie;
  const brakWskazania = czyWlasciciel && wskazana === undefined;

  /*
   * Podział na wyjazdy liczy SERWER — ekran go tylko czyta. Pole bywa nieobecne (starszy
   * serwer), więc każde użycie idzie przez `?.`: brak podziału ma znaczyć „pokaż cały dzień",
   * a nie „wywróć ekran roboczy kuchni w trakcie wydawania posiłków".
   */
  const w = data?.wyjazdy;
  // Nagłówek wydruku listy posiłków (OWNER 19.10): nazwa z serwera, inaczej numer restauracji.
  const nazwaDoWydruku = data?.restauracja_nazwa?.trim() || (data ? `Restauracja nr ${data.restauracja_id}` : 'Restauracja');
  const podzialZnany = (w?.pierwszy.podsumowanie.pozycji ?? 0) + (w?.drugi.podsumowanie.pozycji ?? 0) > 0;
  const bezCutoffu = w?.bez_cutoffu.podsumowanie.posilkow ?? 0;

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Dzisiejsze zamówienia"
        opis="Co i ile ugotować, i do której szkoły to jedzie."
      />

      <Karta>
        <Stack direction="row" spacing={2} useFlexGap sx={{ p: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField label="Dzień dostawy" type="date" size="small" value={dzien}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDzien(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
          {czyWlasciciel && (
            <TextField label="Restauracja (nr)" size="small" sx={{ width: 180 }} value={restauracja}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRestauracja(e.target.value)}
              helperText="Konto właściciela nie ma własnej restauracji" />
          )}
          {/*
            Warunek pyta o SAMO POLE, nie o obecność odpowiedzi.
            `data && data.podstawa.dzien_liczony_wg` wywracało cały ekran, gdy odpowiedź
            przyszła bez `podstawa` — a to jest ekran roboczy kuchni, używany w trakcie
            wydawania posiłków. Brak jednego podpisu nie może zabrać całej listy.
          */}
          {data?.podstawa?.dzien_liczony_wg && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {data.podstawa.dzien_liczony_wg}
            </Typography>
          )}
        </Stack>

        <Stack sx={{ px: 2, pb: 2 }}>
          <StanOdswiezania
            pobranoO={zapytanie.dataUpdatedAt}
            odswiezanie={zapytanie.isFetching}
            bladOdswiezania={zapytanie.isError}
            nieudanychProb={zapytanie.failureCount}
            onOdswiez={() => { void zapytanie.refetch(); }}
          />        </Stack>
      </Karta>

      {brakWskazania && (
        <EmptyState
          title="Wskaż restaurację"
          description="Konto właściciela nie jest przypisane do restauracji, więc trzeba podać, czyj panel oglądasz."
        />
      )}

      {!brakWskazania && isLoading && <LoadingState />}
      {!brakWskazania && error ? <ErrorState error={error} context="Panel restauracji — dzisiejsze zamówienia." /> : null}

      {/*
        ┌─ WARUNEK PYTA O PODSUMOWANIE, NIE O SAMĄ ODPOWIEDŹ ────────────────────────────────┐
        │ Wcześniej stało tu `data &&`, a w środku `p!` — wykrzyknik uciszał TypeScript,     │
        │ ale nie zmieniał tego, co dzieje się w przeglądarce. Odpowiedź bez `podsumowanie`  │
        │ wywracała CAŁY ekran roboczy kuchni w trakcie wydawania posiłków.                  │
        │                                                                                    │
        │ Teraz brak podsumowania znaczy „nie ma czego pokazać", a nie „aplikacja padła".    │
        └────────────────────────────────────────────────────────────────────────────────────┘
      */}
      {data && p && (
        <>
          {/* ── PODSUMOWANIE OPERACYJNE ─────────────────────────────────────────────────── */}
          <Karta>
            <Stack direction="row" spacing={4} useFlexGap sx={{ p: 2, flexWrap: 'wrap' }}>
              <Kpi etykieta="Zamówienia" wartosc={p.zamowien} />
              <Kpi etykieta="Posiłki do przygotowania" wartosc={p.posilkow} opis="suma sztuk" />
              <Kpi etykieta="Szkoły" wartosc={p.szkol} opis="dokąd dziś jedzie" />
            </Stack>
          </Karta>

          {/*
            Pozycje bez daty dostawy NIE są wliczone. Cicho pominięte zamówienie to zamówienie
            niezrealizowane — mówimy o nim wprost, zamiast pozwolić mu zniknąć w zerze.
          */}
          {data.pozycji_bez_daty_dostawy > 0 && (
            <Alert severity="warning">
              <strong>{data.pozycji_bez_daty_dostawy}</strong>{' '}
              {data.pozycji_bez_daty_dostawy === 1 ? 'pozycja nie ma' : 'pozycji nie ma'} zapisanej
              daty dostawy i {data.pozycji_bez_daty_dostawy === 1 ? 'nie została' : 'nie zostały'}{' '}
              wliczone do tego dnia. Nie wiemy, kiedy jadą.
            </Alert>
          )}

          {p.pozycji === 0 && (
            <EmptyState
              title="Brak zamówień na ten dzień"
              description="Na wskazany dzień dostawy nie ma zrealizowanych zamówień. To nie jest błąd — sprawdź inny dzień."
            />
          )}

          {p.pozycji > 0 && (
            <>
              {/* ── DWA KURSY KIEROWCY ───────────────────────────────────────────────── */}
              <Karta>
                <PasSekcji tytul="Podział na wyjazdy" wariant="cichy" />
                <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', display: 'block' }}>
                  {w?.regula ?? `Godzina ${GRANICA_DOMYSLNA} należy do pierwszego wyjazdu.`}{' '}
                  Danie dziedziczy kurs po szkole SWOJEJ pozycji — to samo danie potrafi jechać
                  w obu wyjazdach i wtedy stoi w obu wykresach z własną liczbą sztuk.
                </Typography>

                {podzialZnany ? (
                  /*
                    2×2 na monitorze, jedna kolumna na wąskim ekranie. Wiersz = wyjazd,
                    kolumna = pytanie („dokąd" / „czego ile"). Ten układ czyta się wzrokiem
                    w pionie: najpierw cały pierwszy kurs, potem cały drugi.
                  */
                  <Box sx={{
                    display: 'grid', gap: 2, p: 2,
                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                  }}>
                    <WykresWyjazdu
                      tytul="Szkoły — 1. wyjazd"
                      podtytul={opisWyjazdu(w?.pierwszy, `zamknięcie do ${w?.granica ?? GRANICA_DOMYSLNA}`)}
                      pozycje={(w?.pierwszy.wg_szkol ?? []).map((s) => ({
                        nazwa: s.szkola, wartosc: s.posilkow, klucz: String(s.szkola_id),
                      }))}
                      pusty="Pierwszy wyjazd bez zamówień w tym dniu."
                    />
                    <WykresWyjazdu
                      tytul="Posiłki — 1. wyjazd"
                      podtytul="Ile czego ugotować na pierwszy kurs"
                      pozycje={(w?.pierwszy.wg_dan ?? []).map((d) => ({ nazwa: d.danie, wartosc: d.sztuk }))}
                      pusty="Pierwszy wyjazd bez pozycji."
                      onDrukuj={() => drukujPosilki(`Posiłki — zamówienia do ${w?.granica ?? GRANICA_DOMYSLNA}`,
                        (w?.pierwszy.wg_dan ?? []).map((d) => ({ nazwa: d.danie, wartosc: d.sztuk })), nazwaDoWydruku, data?.dzien ?? dzien)}
                    />
                    <WykresWyjazdu
                      tytul="Szkoły — 2. wyjazd"
                      podtytul={opisWyjazdu(w?.drugi, `zamknięcie po ${w?.granica ?? GRANICA_DOMYSLNA}`)}
                      pozycje={(w?.drugi.wg_szkol ?? []).map((s) => ({
                        nazwa: s.szkola, wartosc: s.posilkow, klucz: String(s.szkola_id),
                      }))}
                      pusty="Drugi wyjazd bez zamówień w tym dniu."
                    />
                    <WykresWyjazdu
                      tytul="Posiłki — 2. wyjazd"
                      podtytul="Ile czego ugotować na drugi kurs"
                      pozycje={(w?.drugi.wg_dan ?? []).map((d) => ({ nazwa: d.danie, wartosc: d.sztuk }))}
                      pusty="Drugi wyjazd bez pozycji."
                      onDrukuj={() => drukujPosilki(`Posiłki — zamówienia po ${w?.granica ?? GRANICA_DOMYSLNA}`,
                        (w?.drugi.wg_dan ?? []).map((d) => ({ nazwa: d.danie, wartosc: d.sztuk })), nazwaDoWydruku, data?.dzien ?? dzien)}
                    />
                  </Box>
                ) : (
                  /*
                    Grafik nie zna ANI JEDNEJ godziny zamknięcia dla tego dnia. Cztery puste
                    wykresy byłyby wtedy gorsze niż brak podziału: kuchnia straciłaby ranking,
                    który działał, i dostała cztery zera zamiast odpowiedzi. Mówimy więc wprost,
                    czego brakuje, i pokazujemy CAŁY dzień bez podziału.
                  */
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Nie znamy godziny zamknięcia zamówień dla żadnej szkoły w tym dniu, więc
                      podziału na wyjazdy nie da się policzyć. Poniżej cały dzień razem.{' '}
                      <strong>Nic nie zostało przypisane do wyjazdu „na wyczucie".</strong>
                      {w?.zrodlo_cutoffu ? <> Źródło godzin: {w.zrodlo_cutoffu}.</> : null}
                    </Alert>
                    <Box sx={{
                      display: 'grid', gap: 2,
                      gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                    }}>
                      <WykresWyjazdu
                        tytul="Szkoły — cały dzień"
                        podtytul="bez podziału na wyjazdy"
                        pozycje={data.wg_szkol.map((s) => ({
                          nazwa: s.szkola, wartosc: s.posilkow, klucz: String(s.szkola_id),
                        }))}
                        pusty="Brak szkół w tym dniu."
                      />
                      <WykresWyjazdu
                        tytul="Posiłki — cały dzień"
                        podtytul="bez podziału na wyjazdy"
                        pozycje={data.wg_dan.map((d) => ({ nazwa: d.danie, wartosc: d.sztuk }))}
                        pusty="Brak dań w tym dniu."
                        onDrukuj={() => drukujPosilki('Posiłki — cały dzień (bez podziału na wyjazdy)',
                          data.wg_dan.map((d) => ({ nazwa: d.danie, wartosc: d.sztuk })), nazwaDoWydruku, data.dzien)}
                      />
                    </Box>
                  </Box>
                )}

                {/*
                  Pozycje, których kursu nie znamy, przy ISTNIEJĄCYM podziale. Nie wchodzą do
                  żadnego z czterech wykresów — i właśnie dlatego muszą być widoczne osobno.
                */}
                {podzialZnany && bezCutoffu > 0 && (
                  <Alert severity="warning" sx={{ mx: 2, mb: 2 }}>
                    <strong>{formatCount(bezCutoffu)}</strong>{' '}
                    {bezCutoffu === 1 ? 'posiłek jedzie' : 'posiłków jedzie'} do{' '}
                    {formatCount(w?.bez_cutoffu.podsumowanie.szkol ?? 0)}{' '}
                    {(w?.bez_cutoffu.podsumowanie.szkol ?? 0) === 1 ? 'szkoły' : 'szkół'}, dla
                    których grafik nie podaje godziny zamknięcia zamówień — nie ma ich w żadnym
                    z czterech wykresów powyżej.{' '}
                    {(w?.bez_cutoffu.wg_szkol ?? []).map((s) => s.szkola).join(' · ')}
                  </Alert>
                )}
              </Karta>

              {/* ── ZAMÓWIENIA WG SZKÓŁ ──────────────────────────────────────────────── */}
              <Karta>
                <PasSekcji tytul="Zamówienia wg szkół" wariant="cichy" />

                {/* Wykres pokazuje proporcję. Tabela mówi, ILE DOKŁADNIE — i to ona jest robocza. */}
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Zamówienia wg szkół — liczby">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Zamówienia</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Posiłki</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.wg_szkol.map((s) => (
                        <TableRow key={s.szkola_id ?? s.szkola} hover>
                          <TableCell>{s.szkola}</TableCell>
                          <TableCell align="right">{formatCount(s.zamowien)}</TableCell>
                          <TableCell align="right"><strong>{formatCount(s.posilkow)}</strong></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Karta>

              {/* ── ROZBICIE SZKOŁA → DANIE ──────────────────────────────────────────── */}
              <Karta>
                <PasSekcji tytul="Co do której szkoły" wariant="cichy" />
                <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
                  {/* §11: to NIE ma być schowane pod najazdem — przy tym się pakuje. */}
                  Rozpiska do pakowania. Każda szkoła osobno, z liczbą sztuk każdego dania.
                </Typography>

                <Stack spacing={0} sx={{ p: 2 }}>
                  {data.rozbicie_szkola_danie.map((s, i) => (
                    <Box key={s.szkola_id ?? s.szkola}>
                      {i > 0 && <Divider sx={{ my: 1.5 }} />}
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
                        <Typography variant="subtitle1">{s.szkola}</Typography>
                        <Chip size="small" label={`razem ${formatCount(s.razem)} szt.`} />
                      </Stack>
                      <Stack component="ul" spacing={0.25} sx={{ pl: 2, m: 0, listStyle: 'none' }}>
                        {s.dania.map((d) => (
                          <Stack key={d.danie} component="li" direction="row" spacing={1}
                            sx={{ alignItems: 'baseline', justifyContent: 'space-between', maxWidth: 520 }}>
                            <Typography variant="body2">{d.danie}</Typography>
                            <Typography variant="body2"><strong>{formatCount(d.sztuk)}</strong></Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Karta>
            </>
          )}
        </>
      )}
    </Stack>
  );
}

export default DzisiejszeZamowieniaPage;
export { DNI_SKROT };
